import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getFileUploads, getPrivateConfig } from '../../../reducers';
import ConfirmButton from '../../buttons/ConfirmButton';
import { FileUploadState } from '../../../reducers/fileUploads';
import { uploadFile } from '../../../actions/fileUploads';
import useAuthentication from '../../../hooks/useAuthentication';
import { cancelTokenSourceType, fileUploadVariablesType } from '../../../types';
import useEffectOnUpdate from '../../../hooks/useEffectOnUpdate';
import { createErrorMessage } from '../../errors/Errors';
import './_styles_/UploadFileRow.css';

export const getFileUploadStateClass = (fileUploadState) => {
    let result;
    switch (fileUploadState) {
        case FileUploadState.INIT:
            result = 'init';
            break;
        case FileUploadState.IN_PROGRESS:
            result = 'upload-in-progress';
            break;
        case FileUploadState.CANCELLED:
            result = 'upload-cancelled';
            break;
        case FileUploadState.COMPLETED:
            result = 'upload-completed';
            break;
        case FileUploadState.ERROR:
            result = 'upload-error';
            break;
        default:
            throw new Error(`Unknown file upload state: ${fileUploadState}`);
    }
    return result;
};

UploadFileRow.propTypes = {
    fileDescriptor: PropTypes.shape({
        file: PropTypes.instanceOf(File),
        isTooLarge: PropTypes.bool,
        cancelTokenSource: cancelTokenSourceType,
    }).isRequired,
    uploadPath: PropTypes.string.isRequired,
    onRemoveFile: PropTypes.func.isRequired,
    onCancelTokenSourceChanged: PropTypes.func.isRequired,
    variables: fileUploadVariablesType,
    responseComp: PropTypes.func,
    onUpload: PropTypes.func,
};

UploadFileRow.defaultProps = {
    variables: null,
    responseComp: null,
    onUpload: null,
};

function UploadFileRow(props) {
    const {
        fileDescriptor,
        onRemoveFile,
        uploadPath,
        onCancelTokenSourceChanged,
        variables,
        responseComp,
        onUpload,
    } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { accessToken } = useAuthentication();
    const { maxUploadSizePerFile } = useSelector(getPrivateConfig);
    const fileUploads = useSelector(getFileUploads);
    const [cancelTokenSource, setCancelTokenSource] = useState(fileDescriptor.cancelTokenSource);
    useEffectOnUpdate(() => {
        setCancelTokenSource(fileDescriptor.cancelTokenSource);
    }, [fileDescriptor.cancelTokenSource]);
    const {
        progress,
        state,
        errorMessageCode,
        errorMessageParams,
        responseData,
        responseFileName,
    } = fileUploads.find((fileUpload) => fileUpload.fileName === fileDescriptor.file.name);
    const isBeforeUpload = state === FileUploadState.INIT;
    const isInProgress = state === FileUploadState.IN_PROGRESS;
    const isUploadCompleted = state === FileUploadState.COMPLETED;
    const isUploadCancelled = state === FileUploadState.CANCELLED;
    const isUploadError = state === FileUploadState.ERROR;
    const isCompletedWithResponse = isUploadCompleted && responseData && responseComp;
    const retryUpload = () => {
        const newCancelTokenSource = axios.CancelToken.source();
        onCancelTokenSourceChanged(newCancelTokenSource);
        dispatch(uploadFile(fileDescriptor.file, newCancelTokenSource, uploadPath, accessToken, variables, onUpload));
    };
    return (
        <div key={fileDescriptor.file.name} className="upload-popup-dialog--selected-files--row">
            <div className={`upload-popup-dialog--selected-files--row--header${fileDescriptor.isTooLarge ? ' max-upload-file-size-exceeded' : ''}`}>
                <span>{fileDescriptor.file.name}</span>
                {isBeforeUpload && (
                    <ConfirmButton
                        message={t('file-upload.confirm-remove-file', { fileName: fileDescriptor.file.name })}
                        className="button-small button-primary-text remove-file-button"
                        onConfirm={onRemoveFile}
                    >
                        <FontAwesomeIcon icon="times" title={t('file-upload.icon.remove')} />
                    </ConfirmButton>
                )}
                {isInProgress && (
                    <ConfirmButton
                        message={t('file-upload.confirm-cancel-file', { fileName: fileDescriptor.file.name })}
                        className="button-small button-primary-text cancel-file-button"
                        onConfirm={() => cancelTokenSource.cancel(`Upload of file '${fileDescriptor.file.name}' cancelled by user.`)}
                    >
                        <FontAwesomeIcon icon="ban" title={t('file-upload.icon.cancel')} className="cancel-upload-icon" />
                    </ConfirmButton>
                )}
                {isUploadCompleted && (
                    <FontAwesomeIcon icon="check-circle" title={t('file-upload.icon.completed')} className="upload-completed-icon" />
                )}
                {isUploadCancelled && (
                    <ConfirmButton
                        message={t('file-upload.confirm-retry-file', { fileName: fileDescriptor.file.name })}
                        className="button-small button-primary-text retry-file-button"
                        onConfirm={retryUpload}
                    >
                        <FontAwesomeIcon icon="sync-alt" title={t('file-upload.icon.retry')} className="retry-upload-icon" />
                    </ConfirmButton>
                )}
                {isUploadError && (
                    <FontAwesomeIcon icon="exclamation-circle" title={t('file-upload.icon.error')} className="upload-error-icon" />
                )}
            </div>
            {!fileDescriptor.isTooLarge && !isUploadError && (
                <div className={`upload-popup-dialog--selected-files--row--progress ${getFileUploadStateClass(state)}`}>
                    <div className="upload-popup-dialog--selected-files--row--progress--bar" style={{ width: `${progress}%` }} />
                </div>
            )}
            {fileDescriptor.isTooLarge && (
                <div className="validation-error">
                    {createErrorMessage('validation.fileUpload.fileTooLarge', { fileSize: fileDescriptor.file.size, maxUploadSizePerFile })}
                </div>
            )}
            {isUploadError && (
                <div className="validation-error">
                    {createErrorMessage(errorMessageCode, errorMessageParams)}
                </div>
            )}
            {isCompletedWithResponse && (
                <div className="completed-response">
                    {responseComp(responseData, responseFileName)}
                </div>
            )}
        </div>
    );
}

export default UploadFileRow;
